<template>
  <div>
    <vue-c3 :handler="handler"></vue-c3>
  </div>
</template>

<script>

import Vue from 'vue';
import VueC3 from 'vue-c3';
import * as d3 from 'd3';
require('c3/c3.css');

export default {
  name: 'GlucoscoreChart',
  components: {
    VueC3
  },
  props: {
    glucoscore: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      handler: new Vue(),
      names: {},
      scoreValues: {
        red: 83.5,
        orange: 48.5,
        green: 16.5
      },
      redGluco: '#e73434',
      orangeGluco: '#dc7205',
      greenGluco: '#2a940c',
      colorCol: '#7d7e7d',
      smoothColorCol: '#989696',
      glucoscoreColors: [
        '#999999',
        '#FC3731',
        '#FC3731',
        '#FD722B',
        '#FD722B',
        '#FE8D00',
        '#FFC500',
        '#50BA00',
        '#50BA00',
        '#28C18C',
        '#28C18C'
      ],
      colors: [
        '#ffa138'
      ]
    };
  },
  watch: {
    glucoscore: {
      handler: function () {
        this.draw();
      }
    }
  },
  mounted () {
    this.draw();
  },
  computed: {
    getFilteredGlucoscore () {
      // Keep any meal that has either a real sensor score or a predicted-scaled fallback.
      // Real scores take precedence; meals without a sensor score fall back to the predicted value.
      return this.glucoscore
        .filter(item => (item.score !== null && item.score !== undefined && item.score !== '') ||
          (item.predicted_score_scaled !== null && item.predicted_score_scaled !== undefined))
        .map(item => {
          const isPredicted = item.is_predicted === true ||
            (item.score === null || item.score === undefined || item.score === '');
          return {
            value: isPredicted ? item.predicted_score_scaled : item.score,
            isPredicted: isPredicted,
            amplitude: item.amplitude_decimated,
            auc: item.auc_decimated,
            glucoseMax: item.glucose_max_decimated,
            stabilization: item.stabilization_decimated,
            timestamp: item.timestamp,
            type: item.type
          };
        });
    }
  },
  methods: {
    draw () {
      const points = this.getFilteredGlucoscore;
      // Split into two aligned series so real (measured) and predicted (estimated) points are
      // drawn distinctly while sharing the same x axis. Missing values are null per series.
      const realLabel = this.$t('health.summary.glucoscore');
      const predictedLabel = this.$t('health.summary.glucoscorePredicted');
      const options = {
        data: {
          x: 'x',
          xFormat: '%Y-%m-%dT%H:%M:%S.%LZ',
          columns: [
            ['x', ...points.map(item => item.timestamp)],
            [realLabel, ...points.map(item => item.isPredicted ? null : item.value * 10)],
            [predictedLabel, ...points.map(item => item.isPredicted ? item.value * 10 : null)]
          ],
          classes: {
            [predictedLabel]: 'glucoscore-predicted-series'
          }
        },
        point: {
          show: true
        },
        color: {
          pattern: [this.colors[0], this.smoothColorCol]
        },
        axis: {
          x: {
            type: 'timeseries',
            tick: {
              rotate: -30,
              multiline: false,
              format: function (x) {
                let formatSeconds = d3.time.format('%d-%m');
                return formatSeconds(new Date(x));
              }
            },

            height: 50
          },
          y: {
            tick: {
              format: function () { return ''; }
            },
            max: 105,
            min: 5,
            padding: { top: 0, bottom: 0 }
          }
        },
        regions: [
          {axis: 'y', start: 5, end: 15, class: 'scoreRegionY1', opacity: 0.2},
          {axis: 'y', start: 15, end: 25, class: 'scoreRegionY2', opacity: 0.2},
          {axis: 'y', start: 25, end: 35, class: 'scoreRegionY3', opacity: 0.2},
          {axis: 'y', start: 35, end: 45, class: 'scoreRegionY4', opacity: 0.2},
          {axis: 'y', start: 45, end: 55, class: 'scoreRegionY5', opacity: 0.2},
          {axis: 'y', start: 55, end: 65, class: 'scoreRegionY6', opacity: 0.2},
          {axis: 'y', start: 65, end: 75, class: 'scoreRegionY7', opacity: 0.2},
          {axis: 'y', start: 75, end: 85, class: 'scoreRegionY8', opacity: 0.2},
          {axis: 'y', start: 85, end: 95, class: 'scoreRegionY9', opacity: 0.2},
          {axis: 'y', start: 95, end: 105, class: 'scoreRegionY10', opacity: 0.2}
        ],
        legend: {
          show: true
        },
        tooltip: {
          contents: (d) => {
            let meal = this.getFilteredGlucoscore.find(item => new Date(item.timestamp).getTime() === d[0].x.getTime());
            let formatSeconds = d3.time.format('%Y-%m-%d');
            const date = formatSeconds(new Date(d[0].x));
            // Read the value from the matched meal, not d[0]: with two series (real / predicted)
            // the inactive series is null at this x, so d[0].value would be 0 for predicted points.
            const score = meal.value;
            const scoreColor = this.glucoscoreColors[Math.floor(score)];

            let rows = `<tr>
                    <th style="background-color:` + this.colorCol + `; "colspan='2'>${date}</th>
                </tr>
              <tr>
              <th  style="background-color:` + this.colorCol + `;">` + this.$t('health.summary.meal') + `</th>
                <th> ${meal.type} </th>
              </tr>
              <tr>
              <th  style="background-color:` + this.colorCol + `;">` + this.$t('health.summary.glucoscore') +
              (meal.isPredicted ? ` (` + this.$t('health.summary.estimated') + `)` : ``) + `</th>
              <th style="background-color:` + scoreColor + `" colspan="100%">` + score + `</th>
              </tr>`;

            // AUC / peak / stabilization / amplitude come from the sensor and only exist for real
            // (measured) scores. Predicted points show just the estimated glucoscore above.
            if (!meal.isPredicted) {
              const aucColor = this.glucoscoreColors[Math.floor(meal.auc)];
              const peakColor = this.glucoscoreColors[Math.floor(meal.glucoseMax)];
              const stabilizationColor = this.glucoscoreColors[Math.floor(meal.stabilization)];
              const amplitudeColor = this.glucoscoreColors[Math.floor(meal.amplitude)];
              rows += `<tr>
              <th  style="background-color:` + this.smoothColorCol + `;">` + this.$t('health.explorer.glucoscore.auc') + `</th>
              <th style="background-color:` + aucColor + `" colspan="100%">` + meal.auc + `</th>
              </tr>
              <tr>
              <th  style="background-color:` + this.smoothColorCol + `;">` + this.$t('health.explorer.glucoscore.peak') + `</th>
              <th style="background-color:` + peakColor + `" colspan="100%">` + meal.glucoseMax + `</th>
              </tr>
              <tr>
              <th  style="background-color:` + this.smoothColorCol + `;">` + this.$t('health.explorer.glucoscore.stabilization') + `</th>
              <th style="background-color:` + stabilizationColor + `" colspan="100%">` + meal.stabilization + `</th>
              </tr>
              <tr>
              <th  style="background-color:` + this.smoothColorCol + `;">` + this.$t('health.explorer.glucoscore.amplitude') + `</th>
              <th style="background-color:` + amplitudeColor + `" colspan="100%">` + meal.amplitude + `</th>
              </tr>`;
            }

            return `<div class='c3-tooltip-container custom-tooltip'>
             <table class='c3-tooltip'>
             <tbody>` + rows + `
               </tbody>
               </table>
              </div>`;
          }
        }
      };
      this.handler.$emit('init', options);
    }
  }
};
</script>

<style lang="scss">
@import "src/stylesheets/variables";

// Predicted (estimated, macro-only) glucoscore series: dashed line so it reads as an estimate,
// distinct from the solid line used for real sensor-measured scores.
.glucoscore-predicted-series {
  .c3-line {
    stroke-dasharray: 4, 3;
  }
}

.scoreRegionY1 {
  fill: #FC3731;
}

.scoreRegionY2 {
  fill: #FC3731;
}

.scoreRegionY3 {
  fill: #FD722B;
}

.scoreRegionY4 {
  fill: #FD722B;
}

.scoreRegionY5 {
  fill: #FE8D00;
}

.scoreRegionY6 {
  fill: #FFC500;
}

.scoreRegionY7 {
  fill: #50BA00;
}

.scoreRegionY8 {
  fill: #50BA00;
}

.scoreRegionY9 {
  fill: #28C18C;
}

.scoreRegionY10 {
  fill: #28C18C;
}

</style>



// WEBPACK FOOTER //
// src/brand/health/components/summary/GlucoscoreChart.vue